/**
 * 浏览器多页签本地会话注册表：API 不可用或尚未就绪时，仍可在同设备多 Tab 间推导 Owner/Standby。
 */
import type {
  DeviceSessionVO,
  HeartbeatParams,
} from "@eweadn-qiankun/shared-http";
import { STALE_SESSION_THRESHOLD_MS } from "@eweadn-qiankun/shared-http";

const CHANNEL_NAME = "eweadn_device_session_local";
const SNAPSHOT_KEY = "eweadn_device_session_local_snapshot";

type LocalHeartbeatMessage = {
  type: "heartbeat";
  session: DeviceSessionVO;
};

type LocalDisconnectMessage = {
  type: "disconnect";
  sessionId: string;
};

type LocalSyncRequestMessage = {
  type: "sync_request";
  sessionId: string;
};

type LocalChannelMessage =
  | LocalHeartbeatMessage
  | LocalDisconnectMessage
  | LocalSyncRequestMessage;

const localSessions = new Map<string, DeviceSessionVO>();
let channel: BroadcastChannel | null = null;
let initialized = false;
let pageSessionId = "";

function getPageSessionId(): string {
  if (pageSessionId) return pageSessionId;
  if (typeof sessionStorage !== "undefined") {
    const stored = sessionStorage.getItem("ehub_driver_session_id");
    if (stored) {
      pageSessionId = stored;
      return pageSessionId;
    }
  }
  pageSessionId = `local-${Date.now()}`;
  return pageSessionId;
}

function persistSnapshot(): void {
  if (typeof localStorage === "undefined") return;
  try {
    pruneStaleSessions();
    localStorage.setItem(
      SNAPSHOT_KEY,
      JSON.stringify(Array.from(localSessions.values()))
    );
  } catch {
    /* ignore */
  }
}

function hydrateFromSnapshot(): void {
  if (typeof localStorage === "undefined") return;
  try {
    const raw = localStorage.getItem(SNAPSHOT_KEY);
    if (!raw) return;
    const list = JSON.parse(raw) as DeviceSessionVO[];
    if (!Array.isArray(list)) return;
    for (const session of list) {
      if (!session?.sessionId) continue;
      localSessions.set(session.sessionId, session);
    }
  } catch {
    /* ignore */
  }
}

function broadcastLocalSessions(): void {
  if (!channel) return;
  for (const session of localSessions.values()) {
    try {
      channel.postMessage({
        type: "heartbeat",
        session,
      } satisfies LocalHeartbeatMessage);
    } catch {
      /* ignore */
    }
  }
}

function ensureChannel(): void {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  hydrateFromSnapshot();

  if (typeof BroadcastChannel === "undefined") return;

  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (ev: MessageEvent<LocalChannelMessage>) => {
      const msg = ev.data;
      if (!msg || typeof msg !== "object") return;
      if (msg.type === "heartbeat" && msg.session?.sessionId) {
        localSessions.set(msg.session.sessionId, msg.session);
        persistSnapshot();
      }
      if (msg.type === "disconnect" && msg.sessionId) {
        localSessions.delete(msg.sessionId);
        persistSnapshot();
      }
      if (msg.type === "sync_request" && msg.sessionId) {
        broadcastLocalSessions();
      }
    };

    try {
      channel.postMessage({
        type: "sync_request",
        sessionId: getPageSessionId(),
      } satisfies LocalSyncRequestMessage);
    } catch {
      /* ignore */
    }
  } catch {
    channel = null;
  }
}

function pruneStaleSessions(nowMs: number = Date.now()): void {
  for (const [id, session] of localSessions.entries()) {
    const t = Date.parse(session.lastHeartbeatAt);
    if (Number.isNaN(t)) continue;
    if (nowMs - t > STALE_SESSION_THRESHOLD_MS) {
      localSessions.delete(id);
    }
  }
}

export function postLocalDeviceSessionHeartbeat(
  params: HeartbeatParams,
  heartbeatAt: string = new Date().toISOString()
): void {
  if (typeof window === "undefined") return;
  ensureChannel();

  const session: DeviceSessionVO = {
    sessionId: params.sessionId,
    sn: params.sn ?? null,
    deviceKey: params.deviceKey,
    vid: params.vid ?? null,
    pid: params.pid ?? null,
    mid: params.mid ?? null,
    cid: params.cid ?? null,
    deviceType: params.deviceType ?? null,
    solutionProvider: params.solutionProvider ?? null,
    clientType: params.clientType ?? null,
    deviceName: params.deviceName ?? null,
    userId: null,
    lastHeartbeatAt: heartbeatAt,
  };

  localSessions.set(session.sessionId, session);
  persistSnapshot();
  try {
    channel?.postMessage({
      type: "heartbeat",
      session,
    } satisfies LocalHeartbeatMessage);
  } catch {
    /* ignore */
  }
}

export function removeLocalDeviceSession(sessionId: string | null): void {
  if (!sessionId) return;
  ensureChannel();
  localSessions.delete(sessionId);
  persistSnapshot();
  try {
    channel?.postMessage({
      type: "disconnect",
      sessionId,
    } satisfies LocalDisconnectMessage);
  } catch {
    /* ignore */
  }
}

export function getLocalDeviceSessions(): DeviceSessionVO[] {
  ensureChannel();
  pruneStaleSessions();
  return Array.from(localSessions.values());
}

export function resetLocalDeviceSessionRegistry(): void {
  localSessions.clear();
  if (typeof localStorage !== "undefined") {
    try {
      localStorage.removeItem(SNAPSHOT_KEY);
    } catch {
      /* ignore */
    }
  }
}
